import React, { useState } from 'react';
import { Modal, Input, InputNumber, Radio, Form, message } from 'antd';
import { DVIInspectionItem, ConvertToLineItemInput, DVILineItemType } from '../../types/dvi';

export interface DVITapToConvertModalProps {
  open: boolean;
  item: DVIInspectionItem;
  estimateId?: number;
  onCancel: () => void;
  onConvert: (input: ConvertToLineItemInput) => Promise<void>;
}

export const DVITapToConvertModal: React.FC<DVITapToConvertModalProps> = ({
  open, item, estimateId, onCancel, onConvert,
}) => {
  const [lineItemType, setLineItemType] = useState<DVILineItemType>(
    estimateId ? 'estimate' : 'work_order',
  );
  const [concern, setConcern] = useState(item.point_name);
  const [cause, setCause] = useState('');
  const [correction, setCorrection] = useState('');
  const [hours, setHours] = useState<number | undefined>(undefined);
  const [laborRate, setLaborRate] = useState<number | undefined>(120);
  const [submitting, setSubmitting] = useState(false);

  const okText = lineItemType === 'estimate' ? 'Add to estimate' : 'Add to work order';

  const handleOk = async () => {
    if (lineItemType === 'estimate' && !estimateId) {
      message.error('No estimate exists for this work order — choose Work Order');
      return;
    }
    setSubmitting(true);
    try {
      const input: ConvertToLineItemInput = {
        lineItemType,
        ...(lineItemType === 'estimate' ? { estimateId } : {}),
        concern,
        cause: cause || undefined,
        correction: correction || undefined,
        hours, laborRate,
      };
      await onConvert(input);
    } catch (err: any) {
      message.error(err?.response?.data?.error ?? 'Failed to add line item');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`Add to ${lineItemType === 'estimate' ? 'Estimate' : 'Work Order'}`}
      okText={okText}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={submitting}
    >
      <Form layout="vertical">
        <Form.Item label="Target">
          <Radio.Group
            value={lineItemType}
            onChange={e => setLineItemType(e.target.value as DVILineItemType)}
          >
            <Radio value="estimate" disabled={!estimateId}>Estimate</Radio>
            <Radio value="work_order">Work Order</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="Concern">
          <Input
            id="concern"
            aria-label="Concern"
            value={concern}
            onChange={e => setConcern(e.target.value)}
          />
        </Form.Item>
        <Form.Item label="Cause (optional)">
          <Input.TextArea rows={2} value={cause} onChange={e => setCause(e.target.value)} />
        </Form.Item>
        <Form.Item label="Correction (optional)">
          <Input.TextArea rows={2} value={correction} onChange={e => setCorrection(e.target.value)} />
        </Form.Item>
        <Form.Item label="Labor hours">
          <InputNumber min={0} step={0.25} value={hours} onChange={v => setHours(v ?? undefined)} />
        </Form.Item>
        <Form.Item label="Labor rate ($/hr)">
          <InputNumber min={0} value={laborRate} onChange={v => setLaborRate(v ?? undefined)} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DVITapToConvertModal;
